import { useEffect, useState } from 'react';
import { Key, ShieldCheck, SignOut, Lock } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { OTP_LENGTH } from '@mybusinessmate/schema';
import { authClient } from '../../lib/api';
import { useTwoFactorSetup } from '../../lib/use-two-factor-setup';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { OtpInput } from '../ui/otp-input';
import { textLinkClass } from '../ui/text-link';
import { useToast } from '../ui/toast';

const FOOTER = 'grid grid-cols-2 gap-(--mbm-space-3)';
const NOTE = 'text-body-xsmall text-text-tertiary';

/** The numbered two-column list of backup codes (Figma A16.2). `used` codes are greyed out. */
export function BackupCodeGrid({ codes, used = [] }: { codes: string[]; used?: string[] }) {
  return (
    <ol className="grid grid-cols-2 gap-x-(--mbm-space-6) gap-y-(--mbm-space-3) rounded-(--mbm-radius-md) bg-surface-muted p-(--mbm-space-5)">
      {codes.map((code, i) => (
        <li
          key={code}
          className={`flex gap-(--mbm-space-3) text-code ${used.includes(code) ? 'text-text-disabled' : 'text-text-primary'}`}
        >
          <span className="w-(--mbm-space-5) text-right text-text-tertiary">{i + 1}.</span>
          {code}
        </li>
      ))}
    </ol>
  );
}

function CodeActions({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      // Clipboard can be blocked (insecure context / denied): the codes are still on screen to write down.
    }
  };
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([`MyBusinessMate backup codes\n\n${codes.join('\n')}\n`], { type: 'text/plain' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mybusinessmate-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="flex items-center justify-between">
      <button type="button" className={textLinkClass} onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy all'}
      </button>
      <button type="button" className={textLinkClass} onClick={download}>
        Download as .txt
      </button>
    </div>
  );
}

export interface BackupCodesDialogProps {
  open: boolean;
  onClose: () => void;
  codes: string[];
  /** `save` = right after turning two-step on; `view` = opened later from Security. */
  variant: 'save' | 'view';
  /** Only for `view`: replaces the set (password already confirmed by the caller). */
  onGetNew?: () => void;
  onBack?: () => void;
  total?: number;
}

/** "Save your backup codes" (after setup) and "Your backup codes" (view) from Figma A16.2. */
export function BackupCodesDialog({
  open,
  onClose,
  codes,
  variant,
  onGetNew,
  onBack,
  total = 10,
}: BackupCodesDialogProps) {
  const saving = variant === 'save';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon={Key}
      title={saving ? 'Save your backup codes' : 'Your backup codes'}
      description={
        saving
          ? 'If you lose your phone, these get you back in. Each code works once. You can see them again in Settings › Security.'
          : `Use one of these if you can’t get a code from your app. Each code works once. ${codes.length} of ${total} left.`
      }
    >
      <BackupCodeGrid codes={codes} />
      <CodeActions codes={codes} />
      <div className={FOOTER}>
        {saving ? (
          <>
            <Button variant="secondary" size="compact" onClick={onBack ?? onClose}>
              Back
            </Button>
            <Button size="compact" onClick={onClose}>
              I’ve saved them
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="compact" onClick={onGetNew}>
              Get new codes
            </Button>
            <Button size="compact" onClick={onClose}>
              Done
            </Button>
          </>
        )}
      </div>
      <p className={NOTE}>
        {saving
          ? 'Two-step sign-in turns on once you confirm. Next time you sign in, we’ll ask for a code from your app.'
          : 'Getting new codes turns off all of these. Keep them somewhere safe, like your password manager.'}
      </p>
    </Dialog>
  );
}

type SetupStep = 'scan' | 'codes';

/**
 * Turn on two-step: scan the QR code, type the first 6-digit code, then save the backup codes (Figma A16.2).
 * `password` is the one the person just confirmed; Better Auth needs it to start enrolling.
 */
export function TwoFactorSetupDialog({
  open,
  password,
  onClose,
  onEnabled,
}: {
  open: boolean;
  password: string | undefined;
  onClose: () => void;
  onEnabled: () => void;
}) {
  const [step, setStep] = useState<SetupStep>('scan');
  const setup = useTwoFactorSetup();
  const { start, reset: resetSetup } = setup;
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  // The person already confirmed their password, so enrolling starts as soon as the dialog opens.
  useEffect(() => {
    if (!open || !password) return;
    let cancelled = false;
    setStep('scan');
    setCode('');
    setError(undefined);
    resetSetup();
    void start(password).then((problem) => {
      if (!cancelled && problem) setError(problem);
    });
    return () => {
      cancelled = true;
    };
  }, [open, password, start, resetSetup]);

  const verify = async (value: string) => {
    setBusy(true);
    setError(undefined);
    const problem = await setup.verify(value);
    setBusy(false);
    if (problem) {
      setError(problem);
      return;
    }
    onEnabled();
    setStep('codes');
  };

  if (step === 'codes') {
    return (
      <BackupCodesDialog
        open={open}
        onClose={onClose}
        codes={setup.backupCodes}
        variant="save"
        onBack={() => {
          setCode('');
          setStep('scan');
        }}
      />
    );
  }

  const { secret, qr, totpUri } = setup;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon={ShieldCheck}
      title="Turn on two-step sign-in"
      description="Scan this QR code with an authenticator app like Google Authenticator or Authy, then type the 6-digit code it shows."
    >
      <div className="flex gap-(--mbm-space-4) rounded-(--mbm-radius-md) bg-surface-muted p-(--mbm-space-4)">
        {qr ? (
          <img
            src={qr}
            alt="QR code for your authenticator app"
            className="size-(--mbm-size-avatar-xl) shrink-0 rounded-(--mbm-radius-xs) bg-surface-card sm:size-(--mbm-layout-qr-size)"
          />
        ) : (
          <div className="size-(--mbm-size-avatar-xl) shrink-0 rounded-(--mbm-radius-xs) bg-surface-tint sm:size-(--mbm-layout-qr-size)" />
        )}
        <div className="flex min-w-0 flex-col justify-center gap-(--mbm-space-1)">
          <p className="text-label-medium text-text-primary">Can’t scan it?</p>
          <p className="text-body-xsmall text-text-tertiary">Enter this key in your app:</p>
          <p className="break-all text-code text-text-primary">{secret}</p>
          <button
            type="button"
            className={`${textLinkClass} self-start`}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(secret);
                setCopiedKey(true);
              } catch {
                // Clipboard blocked: the key is visible to copy by hand.
              }
            }}
          >
            {copiedKey ? 'Copied' : 'Copy key'}
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-(--mbm-space-2)">
        <p className="text-label-medium text-text-primary">6-digit code from your app</p>
        <OtpInput
          value={code}
          onChange={(v) => {
            setCode(v);
            setError(undefined);
          }}
          onComplete={(c) => void verify(c)}
          disabled={busy || !totpUri}
          error={Boolean(error)}
          autoFocus
        />
        {error ? (
          <p role="alert" className="text-body-small text-feedback-error">
            {error}
          </p>
        ) : null}
      </div>
      <div className={FOOTER}>
        <Button variant="secondary" size="compact" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          size="compact"
          loading={busy}
          disabled={code.length !== OTP_LENGTH || !totpUri}
          onClick={() => void verify(code)}
        >
          Verify & continue
        </Button>
      </div>
    </Dialog>
  );
}

/**
/**
 * "Turn off two-step verification?" asks for a code from the authenticator app (Figma A16.2), and the password
 * the caller already confirmed is what Better Auth needs to actually switch it off.
 * When the workspace requires two-step (staff only), this is replaced by "Two-step is required for staff".
 */
export function TurnOffTwoFactorDialog({
  open,
  password,
  requiredByPolicy = false,
  audience = 'client',
  onClose,
  onMoveToNewPhone,
  onDisabled,
}: {
  open: boolean;
  password: string | undefined;
  /** The workspace requires two-step from this person (staff only), so it can only move to a new phone. */
  requiredByPolicy?: boolean;
  audience?: 'client' | 'staff';
  onClose: () => void;
  onMoveToNewPhone: () => void;
  onDisabled: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (requiredByPolicy) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        icon={Lock}
        title="Two-step is required for staff"
        description="Your admin requires two-step verification for everyone on the team, so it can’t be turned off. Got a new phone? Move it to the new phone instead."
      >
        <div className={FOOTER}>
          <Button variant="secondary" size="compact" onClick={onClose}>
            Close
          </Button>
          <Button size="compact" onClick={onMoveToNewPhone}>
            Move to a new phone
          </Button>
        </div>
        <p className={NOTE}>Only an admin can change this, in Workspace › Security policy.</p>
      </Dialog>
    );
  }

  const turnOff = async () => {
    if (!password) return;
    setBusy(true);
    setError(undefined);
    const verified = await authClient.twoFactor.verifyTotp({ code });
    const wrong = mapAuthError(verified.error);
    if (wrong) {
      setBusy(false);
      setError(
        wrong.kind === 'invalid_code' ? 'That code didn’t work. Try the newest one in your app.' : wrong.message,
      );
      return;
    }
    const { error: authError } = await authClient.twoFactor.disable({ password });
    setBusy(false);
    const mapped = mapAuthError(authError);
    if (mapped) {
      setError(mapped.message);
      return;
    }
    setCode('');
    onDisabled();
    toast({ title: 'Two-step verification is off' });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon={ShieldCheck}
      title="Turn off two-step verification?"
      description={
        audience === 'staff'
          ? 'Anyone who gets your password could then sign in to your staff account and see client details. Your backup codes will stop working.'
          : 'Anyone who gets your password could then sign in to your account. Your backup codes will stop working.'
      }
    >
      <div className="flex flex-col gap-(--mbm-space-2)">
        <p className="text-label-medium text-text-primary">Code from your authenticator app</p>
        <OtpInput
          value={code}
          onChange={(v) => {
            setCode(v);
            setError(undefined);
          }}
          disabled={busy}
          error={Boolean(error)}
          autoFocus
        />
        {error ? (
          <p role="alert" className="text-body-small text-feedback-error">
            {error}
          </p>
        ) : null}
      </div>
      <div className={FOOTER}>
        <Button variant="secondary" size="compact" onClick={onClose} disabled={busy}>
          Keep it on
        </Button>
        <Button size="compact" loading={busy} disabled={code.length !== OTP_LENGTH} onClick={() => void turnOff()}>
          Turn off
        </Button>
      </div>
      {/* TODO(api): the "we'll email you" confirmation needs the notification service; the audit trail entry is written by the API. */}
      <p className={NOTE}>We’ll email you and log this in the audit trail.</p>
    </Dialog>
  );
}

/** "Sign out of 2 other devices?" (Figma A16.2). */
export function SignOutOthersDialog({
  open,
  deviceNames,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  deviceNames: string[];
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const count = deviceNames.length;
  const list =
    deviceNames.length > 1
      ? `${deviceNames.slice(0, -1).join(', ')} and ${deviceNames.at(-1)}`
      : (deviceNames[0] ?? '');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon={SignOut}
      title={`Sign out of ${count} other ${count === 1 ? 'device' : 'devices'}?`}
      description={`You’ll stay signed in on this browser. ${list} will need to sign in again.`}
    >
      {count === 0 ? <Banner tone="info">You’re only signed in here.</Banner> : null}
      <div className={FOOTER}>
        <Button variant="secondary" size="compact" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button size="compact" loading={busy} disabled={count === 0} onClick={onConfirm}>
          Sign out {count} {count === 1 ? 'device' : 'devices'}
        </Button>
      </div>
    </Dialog>
  );
}
